import { useState, type ReactNode } from "react";
import { ChevronDown, Clock, ListChecks, Info, AlertTriangle } from "lucide-react";
import { factsFor } from "@/config/toolFacts";
import { Badge } from "@/components/ui/badge";
import { PrivacyNote } from "./PrivacyNote";

const METHOD_LABEL = { ai: "Written by AI — check important details", rules: "Fixed rules, no AI — same input gives the same result", mixed: "Fixed rules plus AI suggestions" } as const;

const KEY = (id: string) => `gb-tool-intro-collapsed:${id}`;

/** Compact "Before you start" card shown above a specialty tool. */
export function ToolIntro({ id, children }: { id: string; children: ReactNode }) {
  const facts = factsFor(id);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(KEY(id)) !== "1"; } catch { return true; }
  });
  if (!facts) return <>{children}</>;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(KEY(id), next ? "0" : "1"); } catch { /* ignore */ }
  };

  return (
    <>
      <section aria-label={`About ${facts.name}`} className="mx-auto max-w-6xl px-4 pt-4">
        {facts.setupNote && (
          <div role="status" className="mb-3 flex gap-2 rounded-lg border border-border bg-muted p-3 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-[hsl(var(--category-safety))]" aria-hidden />
            <p><strong>Not available yet.</strong> {facts.setupNote}</p>
          </div>
        )}
        <div className="rounded-lg border border-border bg-card text-card-foreground">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className="focus-ring flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium"
          >
            <Info className="h-4 w-4 text-primary" aria-hidden />
            <span className="flex-1">Before you start</span>
            {facts.maturity !== "Stable" && <Badge variant="outline">{facts.maturity}</Badge>}
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
          </button>
          {open && (
            <div className="grid gap-4 border-t border-border px-4 py-3 text-sm sm:grid-cols-3">
              <div className="sm:col-span-3 text-muted-foreground">
                <p>{facts.does}</p>
                <p className="mt-1 text-xs"><span className="font-medium text-foreground">How results are made:</span> {METHOD_LABEL[facts.method]}</p>
              </div>
              <div>
                <h3 className="mb-1 flex items-center gap-1 font-medium"><ListChecks className="h-4 w-4" aria-hidden />What you need</h3>
                <ul className="list-disc pl-5 text-muted-foreground">{facts.needs.map((n) => <li key={n}>{n}</li>)}</ul>
              </div>
              <div>
                <h3 className="mb-1 flex items-center gap-1 font-medium"><Clock className="h-4 w-4" aria-hidden />How long</h3>
                <p className="text-muted-foreground">{facts.time}</p>
              </div>
              <div>
                <h3 className="mb-1 font-medium">Limits</h3>
                <ul className="list-disc pl-5 text-muted-foreground">{facts.limits.map((l) => <li key={l}>{l}</li>)}</ul>
              </div>
              {facts.uploads && <PrivacyNote kind="upload" className="sm:col-span-3" />}
            </div>
          )}
        </div>
      </section>
      {children}
    </>
  );
}
